export interface PlacePrediction {
    placeId: string
    description: string
    mainText: string
    secondaryText: string
}

export interface PlaceDetails {
    placeId: string
    formatted: string
    lat: number
    lng: number
    city?: string
    region?: string
    country?: string
}

export interface GeocodingResult {
    formatted: string
    lat: number
    lng: number
    city?: string
}

const HAVERSINE_MULTIPLIER = 1.4

export function useGeocoding() {
    function createSessionToken(): string {
        return (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`)
    }

    async function searchPredictions(query: string, sessionToken?: string): Promise<PlacePrediction[]> {
        if (!query || query.length < 2) return []
        try {
            const res = await $fetch<{ predictions: PlacePrediction[] }>('/api/places/autocomplete', {
                method: 'POST',
                body: { query, sessionToken },
            })
            return res.predictions ?? []
        } catch {
            return []
        }
    }

    async function getPlaceDetails(placeId: string, sessionToken?: string): Promise<PlaceDetails | null> {
        if (!placeId) return null
        try {
            return await $fetch<PlaceDetails>('/api/places/details', {
                method: 'POST',
                body: { placeId, sessionToken },
            })
        } catch {
            return null
        }
    }

    async function getDrivingDistance(lat1: number, lng1: number, lat2: number, lng2: number): Promise<number> {
        try {
            const response = await $fetch<any>(
                `https://router.project-osrm.org/route/v1/driving/${lng1},${lat1};${lng2},${lat2}?overview=false`,
                { timeout: 5000 },
            )

            if (response?.routes?.[0]?.distance) {
                return Math.round(response.routes[0].distance / 100) / 10
            }
        } catch {}

        return haversineDistance(lat1, lng1, lat2, lng2) * HAVERSINE_MULTIPLIER
    }

    function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
        const R = 6371
        const dLat = toRad(lat2 - lat1)
        const dLng = toRad(lng2 - lng1)
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2)
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
        return Math.round(R * c * 10) / 10
    }

    function toRad(deg: number): number {
        return deg * (Math.PI / 180)
    }

    return {
        createSessionToken,
        searchPredictions,
        getPlaceDetails,
        getDrivingDistance,
        haversineDistance,
    }
}
